

import React, { useState , useEffect} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Import data and images
import productData from './productData.json';
import { imageMap } from './imageImports';

const InverterProductPage = () => {
  const [currentPage, setCurrentPage] = useState('products');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('technical');
  const navigate = useNavigate();
    useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



// Add this state variable to your component (add to existing useState declarations)
const [expandedSpecCategory, setExpandedSpecCategory] = useState(null);

// Add this toggle function
const toggleSpecCategory = (categoryKey) => {
  setExpandedSpecCategory(prev => 
    prev === categoryKey ? null : categoryKey
  );
};

  // Get image URL from image key
  const getImageUrl = (imageKey) => {
    return imageMap[imageKey] || "/api/placeholder/300/200";
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setCurrentPage('detail');
  };

  const handleBackToProducts = () => {
    setCurrentPage('products');
    setSelectedProduct(null);
  };

  const handleOrderNow = () => {
    navigate('/contact-us');
  };

  const ProductsPage = () => (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
  <motion.h1 
  className="text-4xl font-bold text-center text-gray-800 mb-12 h-60 bg-[#e8e8e8] flex justify-center items-center"
  initial={{ opacity: 0, y: -20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6 }}
>
  Range Of Our Product
</motion.h1>



        {productData.productCategories.map((category, categoryIndex) => (
          <motion.div 
            key={category.id}
            className="mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
          >
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                {category.title}
              </h2>
              <p className="text-gray-600">{category.description}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {category.products.map((product, index) => (
                <motion.div
                  key={product.id}
                  className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.02, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleProductClick(product)}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="bg-gray-200 h-48 flex items-center justify-center">
                    <img 
                      src={getImageUrl(product.image)} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800">{product.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{product.category}</p>
                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">{product.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  // Rest of your component remains the same, but update the data sources
const ProductDetailPage = () => {
  // Helper function to organize specifications into categories
  const organizeSpecifications = (specs) => {
    const categories = {
      electrical: {
        title: "Electrical",
        color: "blue",
        specs: {}
      },
      output: {
        title: "Output",
        color: "green",
        specs: {}
      },
      environmental: {
        title: "Environmental", 
        color: "orange",
        specs: {}
      }
    };

    // Map specifications to categories based on key names
    Object.entries(specs).forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();
      
      if (lowerKey.includes('voltage') && lowerKey.includes('input')) {
        categories.electrical.specs['Input Voltage'] = value;
      } else if (lowerKey.includes('voltage') && lowerKey.includes('output')) {
        categories.output.specs['Output Voltage'] = value;
      } else if (lowerKey.includes('current')) {
        categories.electrical.specs['Current'] = value;
      } else if (lowerKey.includes('power')) {
        categories.output.specs['Power'] = value;
      } else if (lowerKey.includes('efficiency')) {
        categories.output.specs['Efficiency'] = value;
      } else if (lowerKey.includes('frequency')) {
        categories.output.specs['Frequency'] = value;
      } else if (lowerKey.includes('waveform')) {
        categories.output.specs['Waveform'] = value;
      } else if (lowerKey.includes('temp')) {
        categories.environmental.specs['Operating Temperature'] = value;
      } else if (lowerKey.includes('dimensions')) {
        categories.environmental.specs['Dimensions'] = value;
      } else if (lowerKey.includes('weight')) {
        categories.environmental.specs['Weight'] = value;
      } else if (lowerKey.includes('thd')) {
        categories.output.specs['THD'] = value;
      } else if (lowerKey.includes('transfer')) {
        categories.output.specs['Transfer Time'] = value;
      } else if (lowerKey.includes('accuracy')) {
        categories.electrical.specs['Accuracy'] = value;
      } else if (lowerKey.includes('communication')) {
        categories.electrical.specs['Communication'] = value;
      } else if (lowerKey.includes('display')) {
        categories.environmental.specs['Display'] = value;
      } else if (lowerKey.includes('cooling')) {
        categories.environmental.specs['Cooling System'] = value;
      } else if (lowerKey.includes('protection')) {
        categories.electrical.specs['Protection'] = value;
      } else {
        // Default to electrical category for unmapped specs
        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        categories.electrical.specs[formattedKey] = value;
      }
    });

    // Filter out empty categories
    return Object.entries(categories).filter(([, category]) => 
      Object.keys(category.specs).length > 0
    );
  };

  // Helper function to create feature icons
  const getFeatureIcon = (feature) => {
    const lowerFeature = feature.toLowerCase();
    
    if (lowerFeature.includes('protection') || lowerFeature.includes('safety')) {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9"/>
        </svg>
      );
    } else if (lowerFeature.includes('monitoring') || lowerFeature.includes('display') || lowerFeature.includes('communication')) {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M21,16V4H3V16H21M21,2A2,2 0 0,1 23,4V16A2,2 0 0,1 21,18H14L16,21V22H8V21L10,18H3A2,2 0 0,1 1,16V4A2,2 0 0,1 3,2H21Z"/>
        </svg>
      );
    } else if (lowerFeature.includes('efficiency') || lowerFeature.includes('power') || lowerFeature.includes('energy')) {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11,4H13V16L18.5,10.5L19.92,11.92L12,19.84L4.08,11.92L5.5,10.5L11,16V4Z"/>
        </svg>
      );
    } else if (lowerFeature.includes('algorithm') || lowerFeature.includes('smart') || lowerFeature.includes('intelligent')) {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.5,12A1.5,1.5 0 0,1 16,10.5A1.5,1.5 0 0,1 17.5,9A1.5,1.5 0 0,1 19,10.5A1.5,1.5 0 0,1 17.5,12M14.5,8A1.5,1.5 0 0,1 13,6.5A1.5,1.5 0 0,1 14.5,5A1.5,1.5 0 0,1 16,6.5A1.5,1.5 0 0,1 14.5,8M9.5,8A1.5,1.5 0 0,1 8,6.5A1.5,1.5 0 0,1 9.5,5A1.5,1.5 0 0,1 11,6.5A1.5,1.5 0 0,1 9.5,8M6.5,12A1.5,1.5 0 0,1 5,10.5A1.5,1.5 0 0,1 6.5,9A1.5,1.5 0 0,1 8,10.5A1.5,1.5 0 0,1 6.5,12M12,3A9,9 0 0,0 3,12A9,9 0 0,0 12,21A9,9 0 0,0 21,12A9,9 0 0,0 12,3Z"/>
        </svg>
      );
    } else {
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,21.9 9.75,22 9.5,22H9M10,16V19.08L13.08,16H20V4H4V16H10Z"/>
        </svg>
      );
    }
  };

  const organizedSpecs = selectedProduct ? organizeSpecifications(selectedProduct.specifications) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.button
          onClick={handleBackToProducts}
          className="flex items-center gap-2 text-[#0572AF] hover:text-blue-800 mb-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -5 }}
        >
          <ArrowLeft size={20} />
          Back to Products
        </motion.button>

        {/* Product Header Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Image Section */}
            <motion.div 
              className="space-y-4"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Main Product Image */}
              <div className="bg-gray-100 rounded-lg p-8 w-full">
                <img 
                  src={getImageUrl(selectedProduct?.image)} 
                  alt={selectedProduct?.name}
                  className="w-full h-64 object-contain"
                />
              </div>
              
              {/* Thumbnail Images */}
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((index) => (
                  <div key={index} className="bg-gray-100 rounded-lg p-2 aspect-square">
                    <img 
                      src={getImageUrl(selectedProduct?.image)} 
                      alt={`${selectedProduct?.name} view ${index}`}
                      className="w-full h-full object-contain rounded cursor-pointer hover:opacity-75 transition-opacity"
                    />
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div>
                <h1 className="text-4xl font-bold text-gray-800 mb-4">
                  {selectedProduct?.name}
                </h1>
                <p className="text-lg text-gray-600 mb-4">
                  {selectedProduct?.description}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {selectedProduct?.category} - Advanced technology for reliable power solutions.
                </p>
              </div>

              {/* Product Highlights - Dynamic based on key specs */}
              <div className="flex flex-wrap gap-4">
                {selectedProduct?.specifications?.efficiency && (
                  <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full">
                    <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                    <span className="text-sm font-semibold">{selectedProduct.specifications.efficiency}</span>
                  </div>
                )}
                {selectedProduct?.specifications?.inputVoltage && (
                  <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full">
                    <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                    <span className="text-sm font-semibold">{selectedProduct.specifications.inputVoltage}</span>
                  </div>
                )}
                {selectedProduct?.specifications?.power && (
                  <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full">
                    <div className="w-3 h-3 bg-orange-600 rounded-full"></div>
                    <span className="text-sm font-semibold">{selectedProduct.specifications.power}</span>
                  </div>
                )}
              </div>

              <motion.button
                onClick={handleOrderNow}
                className="w-full bg-[#0572AF] text-white py-4 px-8 rounded-lg font-semibold transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Contact Now →
              </motion.button>
            </motion.div>


           
          </div>
        </div>





      
      </div>
{/* Special Features Section */}
{selectedProduct?.specialFeatures && selectedProduct.specialFeatures.length > 0 && (
  <motion.div 
    className="bg-gray-50 rounded-2xl shadow-xl overflow-hidden"
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.4 }}
  >
    <div className="relative px-8 py-6 md:px-16 md:py-1 md:pb-5">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gray-100 rounded-full -translate-y-32 translate-x-32 opacity-20"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gray-200 rounded-full translate-y-24 -translate-x-24 opacity-30"></div>
      
      {/* Header */}
      <div className="text-center mb-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h2 className="text-3xl md:text-3xl font-bold text-black mb-4">
            Special Features
          </h2>
  
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            The <span className="font-semibold text-gray-800">{selectedProduct?.name}</span> incorporates 
            cutting-edge technologies for exceptional performance and unmatched reliability.
          </p>
        </motion.div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
        {selectedProduct.specialFeatures.map((feature, index) => (
          <motion.div 
            key={index}
            className="group relative bg-white rounded-2xl p-8 hover:bg-gray-50  duration-500 border border-gray-200"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            {/* Hover effect */}
            <div className="absolute inset-0 bg-[#EEF6FA] rounded-2xl transition-all duration-500"></div>
            
            {/* Content */}
            <div className="relative z-10">
              {/* Icon */}
              {/* <div className="mb-6">
                <div className="w-16 h-16  text-black rounded-2xl flex items-center justify-center  transition-all duration-300">
                  {getFeatureIcon(feature)}
                </div>
              </div>
               */}
              {/* Feature text */}
              <p className="text-gray-700 font-medium leading-relaxed text-base group-hover:text-gray-800 transition-colors duration-300">
                {feature}
              </p>
              

            </div>
          </motion.div>
        ))}
      </div>
      
    
    </div>
  </motion.div>
)}

        {/* Technical Specifications Section */}
{/* Technical Specifications Section */}
<motion.div 
  className="bg-white rounded-lg shadow-lg mb-8 px-4 sm:px-8 lg:px-28 py-8 sm:py-16 lg:py-5"
  initial={{ opacity: 0, y: 50 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.2 }}
>
  <div className="p-2 sm:p-4 lg:p-8">
    <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-4 sm:mb-8">
      Technical Specifications
    </h2>
   

    {/* Simple 3-column layout */}
    <div className="space-y-6 lg:grid lg:grid-cols-3 lg:gap-8 lg:space-y-0">
      {organizedSpecs.map(([categoryKey, category]) => (
        <div key={categoryKey} className="bg-gray-50 rounded-lg p-4 sm:p-6">
          {/* Simple Header */}
          <h3 className={`text-lg sm:text-xl font-semibold text-${category.color}-600 mb-4`}>
            {category.title}
          </h3>

          {/* Specifications List */}
          <div className="space-y-3">
            {Object.entries(category.specs).map(([specKey, specValue]) => (
              <div key={specKey} className="bg-white rounded p-3 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2">
                  <span className="text-gray-600 text-sm sm:text-base font-medium">
                    {specKey}:
                  </span>
                  <span className="font-semibold text-gray-800 text-sm sm:text-base break-words">
                    {specValue}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
</motion.div>

          {/* CTA Section */}
                  <motion.div 
                    className="bg-[#0572AF] py-16 px-4 text-center"
                   
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                  >
                    <motion.div 
                      className="max-w-4xl mx-auto"
                     
                    >
                      <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Ready to Power Your Critical Systems?
                      </h2>

                      <motion.div 
  className="bg-white py-16 px-4"
  initial={{ opacity: 0, y: 50 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.6 }}
>
  <div className="max-w-2xl mx-auto">
    <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
      Order Now
    </h2>

    <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Full Name */}
      <div>
        <label className="block text-gray-700 font-medium mb-2">Full Name</label>
        <input
          type="text"
          placeholder="Enter here"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Email */}
      <div>
        <label className="block text-gray-700 font-medium mb-2">Email</label>
        <input
          type="email"
          placeholder="Enter email"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Subject */}
      <div>
        <label className="block text-gray-700 font-medium mb-2">Subject</label>
        <input
          type="text"
          placeholder="Enter here"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Mobile */}
      <div>
        <label className="block text-gray-700 font-medium mb-2">Mobile</label>
        <div className="flex">
          <select className="px-3 py-3 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50">
            <option>+91</option>
            <option>+1</option>
            <option>+44</option>
          </select>
          <input
            type="tel"
            placeholder="Enter here"
            className="flex-1 px-4 py-3 border-t border-r border-b border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Message - Full Width */}
      <div className="md:col-span-2">
        <label className="block text-gray-700 font-medium mb-2">Message</label>
        <textarea
          rows="4"
          placeholder="Enter here"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        ></textarea>
      </div>

      {/* Quantity */}
      <div>
        <label className="block text-gray-700 font-medium mb-2">Quantity</label>
        <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
          <option>Select</option>
          <option>1</option>
          <option>2-5</option>
          <option>6-10</option>
          <option>10+</option>
        </select>
      </div>

      {/* Product Code */}
      <div>
        <label className="block text-gray-700 font-medium mb-2">Product Code</label>
        <input
          type="text"
          placeholder="Enter here"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Submit Button - Full Width */}
      <div className="md:col-span-2 flex justify-center mt-6">
        <motion.button
          type="submit"
          className="bg-[#0572AF] text-white px-12 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors min-w-[200px]"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Submit
        </motion.button>
      </div>
    </form>
  </div>
</motion.div>
                  
                    </motion.div>
                  </motion.div>


{/* Order Now Form Section */}

    </div>
  );
};

  // ContactPage remains the same...


  return (
    <AnimatePresence mode="wait">
      {currentPage === 'products' && <ProductsPage key="products" />}
      {currentPage === 'detail' && <ProductDetailPage key="detail" />}
      {/* {currentPage === 'contact' && <ContactPage key="contact" />} */}
    </AnimatePresence>
  );
};

export default InverterProductPage;